import { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	CircularProgress,
	Typography,
	Skeleton,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import AlertsRow from 'features/IRWorkstation/components/Alerts/AlertsRow';

const AlertsRowSkeleton = () => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: '9px',
			padding: '9px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Skeleton
			variant='circular'
			width={7}
			height={7}
			sx={{ mt: '4px', flexShrink: 0 }}
		/>

		<Box sx={{ flex: 1 }}>
			<Skeleton width='55%' height={18} />

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '6px',
					mt: '2px',
				}}
			>
				<Skeleton width={70} height={14} />
				<Skeleton width={4} height={14} />
				<Skeleton width={120} height={14} />
			</Box>
		</Box>
	</Box>
);

const AlertsSkeleton = () => (
	<Box data-testid='alerts-skeleton'>
		{Array.from({ length: 5 }).map((_, index) => (
			<AlertsRowSkeleton key={index} />
		))}
	</Box>
);

const AlertsWidget = ({
	onOpenAlertsDrawer,
	alerts,
	loading,
	loadingMore,
	error,
	hasMore,
	loadMore,
	totalCount,
	alertStatus,
	onAlertStatusChange,
}) => {
	const isEmpty = alerts.length === 0;

	const getBadgeColor = (alertStatus, totalCount) => {
		if (!totalCount) return 'gray';
		if (alertStatus === 'acknowledged') return 'green';

		return 'red';
	};

	const AlertList = () =>
		alerts.map((alert) => (
			<AlertsRow
				key={`alert-${alert.id}`}
				alert={alert}
				onClick={alert ? () => onOpenAlertsDrawer(alert) : undefined}
			/>
		));

	const ErrorMessage = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				height: '100%',
			}}
		>
			<Typography variant='body2' color='error'>
				{error || 'Failed to load alerts.'}
			</Typography>
		</Box>
	);

	const EmptyState = () => {
		const message =
			alertStatus === 'acknowledged'
				? 'No Acknowledged Alerts.'
				: 'No Unacknowledged Alerts.';

		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '40px',
					height: '100%',
				}}
			>
				<Typography variant='body2' color='text.secondary'>
					{message}
				</Typography>
			</Box>
		);
	};

	const handleScroll = useCallback(
		(event) => {
			const element = event.currentTarget;

			const reachedBottom =
				element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

			if (reachedBottom && hasMore && !loading) {
				loadMore();
			}
		},
		[hasMore, loading, loadMore]
	);

	// Update the filter only when a status is selected
	const handleStatusChange = (_, newStatus) => {
		if (newStatus) {
			onAlertStatusChange(newStatus);
		}
	};

	const AlertStatusControls = () => (
		<ToggleButtonGroup
			value={alertStatus}
			exclusive
			onChange={handleStatusChange}
			size='small'
			sx={{
				ml: 2,
				height: 24,
				borderRadius: 2,
				'& .MuiToggleButton-root': {
					px: 3,
					textTransform: 'none',
					borderColor: 'divider',
					backgroundColor: 'background.subtle',
				},
				'& .MuiToggleButton-root.Mui-selected': {
					bgcolor: 'primary.main',
					color: 'primary.contrastText',
					'&:hover': {
						bgcolor: 'primary.dark',
					},
				},
			}}
		>
			<ToggleButton value='unacknowledged'>Unacknowledged</ToggleButton>

			<ToggleButton value='acknowledged'>Acknowledged</ToggleButton>
		</ToggleButtonGroup>
	);

	return (
		<QuadPanel
			title='Alerts'
			widgetControls={<AlertStatusControls />}
			badge={`${totalCount ?? 0} ${alertStatus}`}
			badgeColor={getBadgeColor(alertStatus, totalCount)}
			badgeLoading={loading}
			onScroll={handleScroll}
		>
			{loading && isEmpty ? (
				<AlertsSkeleton />
			) : error ? (
				<ErrorMessage />
			) : isEmpty ? (
				<EmptyState />
			) : (
				<>
					<AlertList />

					{loadingMore && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								py: 2,
							}}
						>
							<CircularProgress size={18} />
						</Box>
					)}
				</>
			)}
		</QuadPanel>
	);
};

AlertsWidget.propTypes = {
	onOpenAlertsDrawer: PropTypes.func.isRequired,
	alerts: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	loadingMore: PropTypes.bool.isRequired,
	error: PropTypes.string,
	hasMore: PropTypes.bool,
	loadMore: PropTypes.func,
	totalCount: PropTypes.number,
	alertStatus: PropTypes.oneOf(['unacknowledged', 'acknowledged']).isRequired,
	onAlertStatusChange: PropTypes.func.isRequired,
};

export default AlertsWidget;
